import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Tag as TagIcon, Trash2, X, Loader2, RefreshCw, Edit2 } from 'lucide-react';
import { crmTagsApi, CrmTag } from '../services/crmApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function Tags() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [tags, setTags] = useState<CrmTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTag, setEditingTag] = useState<CrmTag | null>(null);
  const [formName, setFormName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const fetchTags = async () => {
    setLoading(true);
    try {
      const data = await crmTagsApi.list();
      setTags(data);
    } catch (err) {
      console.error('Failed to fetch tags:', err);
      toast.error('Failed to load tags');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchTags();
  }, []);
  // Filter by team and region scope
  const filteredTags = tags.filter(t => {
    // Team filter: only show tags belonging to current workspace or with no team
    if (t.Team && t.Team !== currentWorkspace.name && t.Team !== team) return false;
    // Region filter
    if (currentSubRegion) {
      return !t.Region || t.Region === currentSubRegion;
    }
    return true;
  });
  const handleOpenCreate = () => {
    setEditingTag(null);
    setFormName('');
    setShowModal(true);
  };
  const handleOpenEdit = (tag: CrmTag) => {
    setEditingTag(tag);
    setFormName(tag.Name || '');
    setShowModal(true);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) return;
    setIsSubmitting(true);
    try {
      const payload: {
        Name: string;
        Team: string;
        Region?: string;
      } = {
        Name: formName.trim(),
        Team: team
      };
      if (currentSubRegion) {
        payload.Region = currentSubRegion;
      }
      if (editingTag) {
        await crmTagsApi.update(editingTag.id, payload);
        toast.success('Tag updated successfully');
      } else {
        await crmTagsApi.create(payload);
        toast.success('Tag created successfully');
      }
      setShowModal(false);
      fetchTags();
    } catch (err) {
      console.error('Failed to save tag:', err);
      toast.error(editingTag ? 'Failed to update tag' : 'Failed to create tag');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      await crmTagsApi.delete(id);
      toast.success('Tag deleted successfully');
      setDeleteConfirm(null);
      fetchTags();
    } catch (err) {
      console.error('Failed to delete tag:', err);
      toast.error('Failed to delete tag');
    } finally {
      setIsDeleting(false);
    }
  };
  return <div className="space-y-6" data-id="element-6823" data-mp="ty47fi3e0obh">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-6824" data-mp="p0kpo87hdtes">
        <div className="min-w-0" data-id="element-6825" data-mp="kzseh9f1tefv">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-6826" data-mp="kyqrq7oo73ib">Tags</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-6827" data-mp="g0xp1omksrms">
            Manage tags for segmenting contacts (synced with Zoho CRM).
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-6828" data-mp="b8e7kgkrpccp">
          <button onClick={fetchTags} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" title="Refresh" data-id="element-6829" data-mp="69un6klto8mg">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-6830" data-mp="3thetgg9ua3tu" />
          </button>
          <button onClick={handleOpenCreate} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-6831" data-mp="3oibzprzb7k16">
            <Plus className="h-4 w-4 flex-shrink-0" data-id="element-6832" data-mp="1int3bp1hbbqm" />
            <span data-id="element-6833" data-mp="1in17qe7vte0c">Create Tag</span>
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-6834" data-mp="1nl2zronacnny">
        {loading && filteredTags.length === 0 ? <div className="p-12 text-center" data-id="element-6835" data-mp="18rgasuho73xg">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-6836" data-mp="1dogeiyr1rule" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-6837" data-mp="zoowmmxetu24">
              Loading tags from CRM...
            </p>
          </div> : filteredTags.length === 0 ? <div className="p-12 text-center" data-id="element-6838" data-mp="14pme0xalik2k">
            <TagIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-6839" data-mp="2u26miglaf30b" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-6840" data-mp="2z1j8z088y82d">
              No tags found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-6841" data-mp="3958114ydevqg">
              {currentSubRegion ? `No tags found for region "${currentSubRegion}".` : 'Create tags to segment your contacts in Zoho CRM.'}
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-6842" data-mp="3hzw4qqoas5d5">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-6843" data-mp="3d6ta0f7sapvd">
              <thead className="bg-gray-50" data-id="element-6844" data-mp="3rvz6vwj5g403">
                <tr data-id="element-6845" data-mp="3ruo5g3d6f8c5">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6846" data-mp="3ru4nimnj0jy4">
                    Tag Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6847" data-mp="4q89znx4r74e">
                    Team
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6848" data-mp="26imr4kruo7jr">
                    Region
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6849" data-mp="22im3c9iv2k01">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6850" data-mp="aeoqlxib6pof">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-6851" data-mp="kepaplo059to">
                {filteredTags.map(tag => <tr key={tag.id} className="hover:bg-gray-50" data-id="element-6852" data-mp="oeq1m5safdhm">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-6853" data-mp="tdu6wlc3cr2o">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200" data-id="element-6854" data-mp="tbzopes7fd2a">
                        <TagIcon className="h-3.5 w-3.5 flex-shrink-0" data-id="element-6855" data-mp="133m6jtz1sae0" />
                        {tag.Name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6856" data-mp="1c4byunp3nimz">
                      {tag.Team || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6857" data-mp="2wo7ewckdt3pp">
                      {tag.Region || 'All Regions'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6858" data-mp="2nn96li2wua1b">
                      {tag.Created_Time ? new Date(tag.Created_Time).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-6859" data-mp="3gcokqgpvgj1g">
                      <div className="flex items-center justify-end gap-2" data-id="element-6860" data-mp="3be4wg9nt2v5x">
                        <button onClick={() => handleOpenEdit(tag)} className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded" title="Edit" data-id="element-6861" data-mp="36j7pd8a36vvo">
                          <Edit2 className="h-4 w-4" data-id="element-6862" data-mp="3uad0ja530zzn" />
                        </button>
                        <button onClick={() => setDeleteConfirm(tag.id)} className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded" title="Delete" data-id="element-6863" data-mp="32mjre79kazxc">
                          <Trash2 className="h-4 w-4" data-id="element-6864" data-mp="2tkkarg2glxux" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Create/Edit Modal */}
      {showModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6865" data-mp="3ccva4zxwndoa">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-6866" data-mp="3ccd2z89pff1l">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} data-id="element-6867" data-mp="3hawstjrf5az8" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-6868" data-mp="2102kpvsl71be">
              <div className="flex items-center justify-between mb-4" data-id="element-6869" data-mp="25uq0n69iyned">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-6870" data-mp="1xeh6n6poqxer">
                  {editingTag ? 'Edit Tag' : 'Create New Tag'}
                </h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-500" data-id="element-6871" data-mp="27dyffx08hrey">
                  <X className="h-5 w-5" data-id="element-6872" data-mp="22fogdbbe26o2" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-6873" data-mp="1eq2fmjfdbjdy">
                <div data-id="element-6874" data-mp="19qpv45byyoej">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6875" data-mp="19riuumcigdn6">
                    Tag Name
                  </label>
                  <input type="text" required className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formName} onChange={e => setFormName(e.target.value)} placeholder="e.g., VIP, Lead, Customer" data-id="element-6876" data-mp="1ogx5ryvqj34o" />
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-6877" data-mp="1supr4u6y99ad">
                  <div data-id="element-6878" data-mp="1sv7s244k4bom">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6879" data-mp="1nvd2q50fju5o">
                      Team
                    </label>
                    <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-6880" data-mp="1iv8lgcrp6vee" />
                  </div>
                  <div data-id="element-6881" data-mp="155nbfrvvlqii">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6882" data-mp="152rbjy0gwrrf">
                      Region
                    </label>
                    <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={currentSubRegion || 'All Regions'} disabled data-id="element-6883" data-mp="v77hzi9dm55u" />
                  </div>
                </div>
                <p className="text-xs text-gray-400" data-id="element-6884" data-mp="36w46w88jhi54">
                  Team and Region are set from your current scope
                </p>
                <div className="flex justify-end gap-3 pt-4" data-id="element-6885" data-mp="3gsqrqx0v8975">
                  <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-6886" data-mp="3gpuu79sxjmj8">
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting || !formName.trim()} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-id="element-6887" data-mp="2xt352jqpid79">
                    {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-6888" data-mp="1xat4q6lju997" />}
                    {editingTag ? 'Update Tag' : 'Create Tag'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6889" data-mp="225f7jldketho">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-6890" data-mp="1ee9ufdz0efb2">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-6891" data-mp="1acofzo6ttsyr" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-6892" data-mp="1o9labzm5lske">
              <div className="flex items-center gap-4 mb-4" data-id="element-6893" data-mp="1jeo1asovjj7i">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-6894" data-mp="2zps0k7khm62m">
                  <Trash2 className="h-5 w-5 text-red-600" data-id="element-6895" data-mp="2ur8co1mc2k7m" />
                </div>
                <div data-id="element-6896" data-mp="gixopt0mwd2n">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-6897" data-mp="gifmmfraj4mt">
                    Delete Tag
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-6898" data-mp="lgpg8lxd9x6b">
                    Are you sure? This will remove the tag from CRM.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3" data-id="element-6899" data-mp="cinhkd13vgm7">
                <button onClick={() => setDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-6900" data-mp="107pzzn3kddhr">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-6901" data-mp="vfor8b5lu4wh">
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-6902" data-mp="3vif6nook8sl9" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}